/**
 * GameScreen Component
 * 
 * Main game screen displaying questions, answer options, progress, and game controls.
 * Handles the core gameplay UI for all subjects.
 * 
 * @module GameScreen
 */

import React, { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import TTSIconButton from '../TTSIconButton';
import FeedbackOverlay from '../FeedbackOverlay';
import AnswerButton from '../AnswerButton';
import MultipleSelectAnswerButton from '../MultipleSelectAnswerButton';
import SectionErrorBoundary from '../SectionErrorBoundary';
import { getCorrectAnswer, isMultipleSelect } from '../../utils/questionUtils';
import { SOUND_TYPES, HAPTIC_TYPES } from '../../constants/audio';
import { TIMEOUTS } from '../../constants/timing';
import { useTouchGestures } from '../../hooks/useTouchGestures';

/**
 * GameScreen - Main game interface for playing questions
 * 
 * @param {Object} props - Component props
 * @param {string} props.currentScreen - Current subject screen identifier
 * @param {Object} props.q - Current question object
 * @param {Array} props.shuffledOptions - Shuffled answer options
 * @param {number} props.currentQuestion - Current question index
 * @param {number} props.questionCount - Total question count
 * @param {number} props.score - Current score
 * @param {boolean} props.hasQuestion - Whether a valid question exists
 * @param {string} props.answerAnimation - Animation class for answer feedback
 * @param {boolean} props.answerLocked - Whether answer selection is locked
 * @param {boolean} props.hintUsed - Whether hint has been used
 * @param {boolean} props.showHint - Whether hint is currently displayed
 * @param {string} props.showFeedback - Feedback state ('correct', 'incorrect', or null)
 * @param {boolean} props.showExplanation - Whether explanation is shown
 * @param {string} props.correctAnswer - Correct answer string
 * @param {Array} props.currentQuestions - Array of all current questions
 * @param {string} props.selectedPhonicsDifficulty - Selected phonics difficulty level
 * @param {Function} props.setSelectedPhonicsDifficulty - Setter for phonics difficulty
 * @param {string} props.selectedMathDifficulty - Selected math difficulty level
 * @param {Function} props.setSelectedMathDifficulty - Setter for math difficulty
 * @param {string} props.selectedReadingCategory - Selected reading category
 * @param {Function} props.setSelectedReadingCategory - Setter for reading category
 * @param {Function} props.setCurrentQuestion - Setter for current question index
 * @param {Function} props.handleAnswer - Handler for answer selection
 * @param {Function} props.handleHelpClick - Handler for help button click
 * @param {Function} props.generateHint - Function to generate hints
 * @param {Function} props.handleFeedbackClose - Handler for feedback overlay close
 * @param {Function} props.navigateTo - Navigation function
 * @param {Function} props.resetGameComplete - Function to reset and return to home
 * @param {Function} props.playSound - Function to play sound effects
 * @param {Function} props.triggerHaptic - Function to trigger haptic feedback
 * @param {Object} props.bgColors - Background color map for screens
 * 
 * @returns {JSX.Element} GameScreen component
 */
const GameScreen = memo(({
  currentScreen,
  q,
  shuffledOptions,
  currentQuestion,
  questionCount,
  score,
  hasQuestion,
  answerAnimation,
  answerLocked,
  hintUsed,
  showHint,
  showFeedback,
  showExplanation,
  correctAnswer,
  currentQuestions,
  selectedPhonicsDifficulty,
  setSelectedPhonicsDifficulty,
  selectedMathDifficulty,
  setSelectedMathDifficulty,
  selectedReadingCategory,
  setSelectedReadingCategory,
  setCurrentQuestion,
  handleAnswer,
  handleHelpClick,
  generateHint,
  handleFeedbackClose,
  navigateTo,
  resetGameComplete,
  playSound,
  triggerHaptic,
  bgColors,
  selectedAnswers,
  handleToggleAnswer,
  handleSubmitMultipleSelect
}) => {
  const qs = currentQuestions;

  // Touch gestures for mobile navigation
  const handlePullToRefresh = useCallback(() => {
    // Refresh game content
    resetGameComplete();
    navigateTo(currentScreen);
    triggerHaptic(HAPTIC_TYPES.SUCCESS);
  }, [resetGameComplete, navigateTo, currentScreen, triggerHaptic]);

  const touchHandlers = useTouchGestures({
    onSwipeLeft: () => {
      // Could navigate to next question or close
      triggerHaptic(HAPTIC_TYPES.LIGHT);
    },
    onSwipeRight: () => {
      // Could go back or navigate to previous
      triggerHaptic(HAPTIC_TYPES.LIGHT);
    },
    onPullToRefresh: handlePullToRefresh
  });

  // Memoize handleAnswer callback to prevent AnswerButton re-renders
  const memoizedHandleAnswer = useCallback((opt, correctAnswer, questions, explanation) => {
    handleAnswer(opt, correctAnswer, questions, explanation);
  }, [handleAnswer]);

  const displayOptions = useMemo(() => {
    const rawOptions = (shuffledOptions && shuffledOptions.length > 0) ? shuffledOptions : (q.options || []);
    if (!rawOptions || rawOptions.length === 0) return [];

    const looksPlaceholder = (opt) => /option\s+[a-d]/i.test(opt) || /^option$/i.test(opt) || /vowel/i.test(opt);

    // If options look reasonable, return as-is
    if (!rawOptions.some(looksPlaceholder)) return rawOptions;

    // Try to rebuild for vowel-team style questions
    if (q.question?.toLowerCase().includes('vowel team') && q.word) {
      const vowelPatterns = ['OA', 'OW', 'AI', 'AY', 'EE', 'EA', 'IE', 'IGH', 'OO', 'OU', 'AU', 'AW', 'OI', 'OY'];
      const match = vowelPatterns.find(p => q.word.toUpperCase().includes(p));
      const correct = match || (q.correct ? String(q.correct).toUpperCase() : null);
      const pool = vowelPatterns.filter(p => p !== correct);
      const distractor = pool[Math.floor(Math.random() * pool.length)] || 'OW';
      return [correct || 'OA', distractor];
    }

    // Default fallback: remove placeholders and ensure at least two options
    const cleaned = rawOptions.filter(opt => !looksPlaceholder(opt));
    if (cleaned.length >= 2) return cleaned;
    cleaned.push('Try again');
    return cleaned;
  }, [shuffledOptions, q]);

  // Determine grid columns based on answer length (for compact display)
  const gridColumns = useMemo(() => {
    if (!displayOptions || displayOptions.length === 0) return 'grid-cols-1';
    
    // Check if all answers are short (numbers, single words, or short phrases)
    const allShort = displayOptions.every(opt => {
      const optStr = String(opt);
      return optStr.length <= 15; // 15 characters or less
    });
    
    // Use 2 columns for short answers, 1 column for longer ones
    return allShort ? 'grid-cols-2' : 'grid-cols-1';
  }, [displayOptions]);

  // Subject-specific selectors
  const subjectSelectors = useMemo(() => {
    const selectors = [];

    if (currentScreen === 'phonics') {
      selectors.push(
        <div key="phonics" className="mb-6">
          <h2 className="text-mobile-lg font-bold text-center mb-4">📚 Choose Difficulty Level</h2>
          <div className="flex justify-center gap-2 xs:gap-3 flex-wrap">
            {['easy', 'medium', 'hard'].map(level => (
              <button
                key={level}
                onClick={() => {
                  setSelectedPhonicsDifficulty(level);
                  setCurrentQuestion(0);
                  playSound(SOUND_TYPES.CLICK);
                  triggerHaptic(HAPTIC_TYPES.LIGHT);
                }}
                className={`btn-touch font-bold text-mobile-sm touch-feedback ${
                  selectedPhonicsDifficulty === level
                    ? 'bg-pink-500 text-white shadow-mobile'
                    : 'bg-pink-100 text-pink-700 hover-mobile'
                }`}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentScreen === 'math') {
      selectors.push(
        <div key="math" className="mb-6">
          <h2 className="text-mobile-lg font-bold text-center mb-4">🔢 Choose Difficulty Level</h2>
          <div className="flex justify-center gap-2 xs:gap-3 flex-wrap">
            {['easy', 'medium', 'hard'].map(level => (
              <button
                key={level}
                onClick={() => {
                  setSelectedMathDifficulty(level);
                  setCurrentQuestion(0);
                  playSound(SOUND_TYPES.CLICK);
                  triggerHaptic(HAPTIC_TYPES.LIGHT);
                }}
                className={`btn-touch font-bold text-mobile-sm touch-feedback ${
                  selectedMathDifficulty === level
                    ? 'bg-blue-500 text-white shadow-mobile'
                    : 'bg-blue-100 text-blue-700 hover-mobile'
                }`}
              >
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </button>
            ))}
          </div>
        </div>
      );
    }

    if (currentScreen === 'reading') {
      selectors.push(
        <div key="reading" className="mb-2">
          <h2 className="text-base md:text-lg font-bold text-center mb-1">📖 Choose Reading Focus</h2>
          <div className="flex justify-center gap-2">
            {[
              { value: 'all', label: 'All Questions' },
              { value: 'story-elements', label: 'Story Elements' },
              { value: 'comprehension', label: 'Comprehension' }
            ].map(option => (
              <button
                key={option.value}
                onClick={() => {
                  setSelectedReadingCategory(option.value);
                  setCurrentQuestion(0);
                  playSound(SOUND_TYPES.CLICK);
                  triggerHaptic(HAPTIC_TYPES.LIGHT);
                }}
                className={`px-3 py-1.5 rounded-xl font-semibold text-sm md:text-sm transition-all transform hover:scale-105 active:scale-95 ${
                  selectedReadingCategory === option.value
                    ? 'bg-green-500 text-white shadow-lg'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return selectors;
  }, [
    currentScreen,
    selectedPhonicsDifficulty,
    setSelectedPhonicsDifficulty,
    selectedMathDifficulty,
    setSelectedMathDifficulty,
    selectedReadingCategory,
    setSelectedReadingCategory,
    setCurrentQuestion,
    playSound,
    triggerHaptic
  ]);

  return (
    <div 
      className={`min-h-screen bg-gradient-to-br ${bgColors[currentScreen] || 'from-purple-200 to-purple-400'} container-mobile ${currentScreen === 'reading' ? 'pt-1 md:pt-2' : ''}`}
      {...touchHandlers}
    >
      {/* Navigation Buttons */}
      <div className="flex justify-between mb-4 gap-2 items-center">
        <button 
          onClick={() => { navigateTo('home'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} 
          className="btn-touch bg-gray-900 text-white shadow-mobile rounded-full px-mobile hover-mobile-scale touch-feedback border border-black/10"
        >
          <span>☰ Menu</span>
        </button>
        {/* Question Counter */}
        <div className="bg-white/90 rounded-full px-4 py-2 shadow-lg border-2 border-purple-300">
          <span className="font-bold text-purple-700 text-sm md:text-base">
            Question {currentQuestion + 1} of {questionCount}
          </span>
        </div>
        <button 
          onClick={() => setCurrentQuestion(0)} 
          className="btn-touch bg-orange-500 text-white font-bold shadow-mobile rounded-full px-mobile hover-mobile-scale touch-feedback"
        >
          <span>🔄</span>
          <span className="hidden xs:inline ml-1">Restart</span>
        </button>
      </div>

      {/* Subject Selectors */}
      {subjectSelectors}

      {/* Main Game Content - Compact */}
      <div className="max-w-xl mx-auto bg-white rounded-2xl shadow-2xl p-2 md:p-4 relative">
        {!hasQuestion && (
          <div className="text-center py-10">
            <div className="text-4xl mb-2">⏳</div>
            <div className="font-bold text-lg mb-2">Loading questions…</div>
            <div className="text-sm text-gray-500 mb-4">If this takes more than a second, tap Restart.</div>
            <button
              onClick={() => {
                resetGameComplete();
                navigateTo(currentScreen);
              }}
              className="bg-purple-500 hover:bg-purple-600 text-white px-4 py-2 rounded-full"
            >
              Restart
            </button>
          </div>
        )}

        {hasQuestion && (
          <>
            <FeedbackOverlay
              feedback={showFeedback}
              visible={!!showFeedback}
              onClose={handleFeedbackClose}
              showExplanation={showExplanation}
              explanation={q?.explanation || ''}
              correctAnswer={correctAnswer}
              score={score}
              isComplete={false}
            />

            {/* Question Display - Compact */}
            <SectionErrorBoundary 
              name="QuestionDisplay"
              title="Question Display Error"
              message="This question couldn't be displayed. Try the next question."
            >
              <div className={`question-container text-center mb-4 ${answerAnimation} bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 rounded-2xl p-3 md:p-4 shadow-lg border-2 border-purple-200`}>
              <div className="text-4xl md:text-5xl mb-2 animate-pulse" style={{ 
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                animation: 'pulse 2s ease-in-out infinite'
              }}>
                {q.image || q.emoji}
              </div>
              {q.word && (
                <div className="flex items-center justify-center gap-2 mb-3">
                  <div className="text-3xl md:text-5xl font-extrabold text-pink-700" style={{
                    textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
                    letterSpacing: '0.05em'
                  }}>
                    {q.word}
                  </div>
                  <TTSIconButton
                    text={q.word}
                    mode="word"
                    className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full transition-all shadow-md flex-shrink-0 text-xl md:text-2xl min-w-[44px] min-h-[44px] border-2 border-white"
                    title="Listen to word"
                    ariaLabel="Listen to word"
                  />
                </div>
              )}
              <div className="flex items-center justify-center gap-2 mb-3 flex-wrap">
                <p className="text-3xl md:text-4xl font-extrabold text-gray-800 leading-tight" style={{
                  textShadow: '1px 1px 3px rgba(0,0,0,0.1)',
                  maxWidth: '90%'
                }}>
                  {q.question}
                </p>
                <TTSIconButton
                  text={q.question}
                  mode="question"
                  className="bg-green-500 hover:bg-green-600 text-white p-2 rounded-full transition-all shadow-md flex-shrink-0 text-xl md:text-2xl min-w-[44px] min-h-[44px] border-2 border-white"
                  title="Listen to question"
                  ariaLabel="Listen to question"
                />
              </div>
              </div>
            </SectionErrorBoundary>

            {/* Answer Buttons - Compact */}
            <SectionErrorBoundary 
              name="AnswerButtons"
              title="Answer Buttons Error"
              message="The answer options couldn't load. Try restarting the game."
            >
              <div className={`grid ${gridColumns} gap-3 md:gap-4 mb-4 px-2 md:px-4 overflow-hidden`}>
              {isMultipleSelect(q) ? (
                // Multiple-select question - use checkbox buttons
                <>
                  {((shuffledOptions && shuffledOptions.length > 0) ? shuffledOptions : (q.options || [])).map((opt, i) => (
                    <MultipleSelectAnswerButton
                      key={`${currentQuestion}-${opt}-${i}`}
                      option={opt}
                      index={i}
                      currentQuestion={currentQuestion}
                      answerLocked={answerLocked}
                      correctAnswers={Array.isArray(getCorrectAnswer(q)) ? getCorrectAnswer(q) : [getCorrectAnswer(q)]}
                      question={q}
                      questions={qs}
                      onAnswer={memoizedHandleAnswer}
                      triggerHaptic={triggerHaptic}
                      selectedAnswers={selectedAnswers || []}
                      onToggle={handleToggleAnswer}
                    />
                  ))}
                  {/* Submit button for multiple-select */}
                  {!answerLocked && (
                    <button
                      onClick={handleSubmitMultipleSelect}
                      disabled={!selectedAnswers || selectedAnswers.length === 0}
                      className={`mt-4 px-8 py-4 rounded-full font-extrabold text-lg md:text-xl transition-all shadow-lg border-4 ${
                        (!selectedAnswers || selectedAnswers.length === 0)
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed border-gray-400'
                          : 'bg-gradient-to-r from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-white hover:scale-110 active:scale-95 border-white'
                      }`}
                      style={{ touchAction: 'manipulation' }}
                      title={(!selectedAnswers || selectedAnswers.length === 0) ? "Select at least one answer" : "Submit your answers"}
                    >
                      {(!selectedAnswers || selectedAnswers.length === 0) 
                        ? 'Select Answers to Submit' 
                        : `Submit ${selectedAnswers.length} Answer${selectedAnswers.length > 1 ? 's' : ''}`}
                    </button>
                  )}
                </>
              ) : (
                // Single-select question - use regular buttons
                ((shuffledOptions && shuffledOptions.length > 0) ? shuffledOptions : (q.options || [])).map((opt, i) => (
                  <AnswerButton
                    key={`${currentQuestion}-${opt}-${i}`}
                    option={opt}
                    index={i}
                    currentQuestion={currentQuestion}
                    answerLocked={answerLocked}
                    correctAnswer={getCorrectAnswer(q)}
                    question={q}
                    questions={qs}
                    onAnswer={memoizedHandleAnswer}
                    triggerHaptic={triggerHaptic}
                  />
                ))
              )}
              </div>
            </SectionErrorBoundary>

            {/* Progress and Score Display */}
            <div className="mt-8 mb-6">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2 px-2">
                  <span className="text-lg md:text-xl font-bold text-purple-700">
                    Question {currentQuestion+1} of {questionCount}
                  </span>
                  <span className="text-lg md:text-xl font-bold text-purple-700">
                    {Math.round(((currentQuestion + 1) / questionCount) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-6 md:h-8 shadow-inner border-2 border-gray-300 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 via-pink-500 to-blue-500 rounded-full transition-all duration-500 ease-out shadow-lg flex items-center justify-end pr-2"
                    style={{ width: `${((currentQuestion + 1) / questionCount) * 100}%` }}
                  >
                    {((currentQuestion + 1) / questionCount) * 100 >= 15 && (
                      <span className="text-white text-xs md:text-sm font-bold">⭐</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="text-center bg-gradient-to-r from-yellow-100 to-orange-100 rounded-2xl p-4 border-4 border-yellow-300 shadow-lg">
                <p className="text-3xl md:text-4xl font-extrabold text-yellow-800" style={{
                  textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
                }}>
                  Score: {score} <span className="text-4xl md:text-5xl">⭐</span>
                </p>
                <p className="text-sm md:text-base text-yellow-700 mt-1 font-semibold">
                  Keep going! You're doing great! 🎉
                </p>
              </div>
            </div>
          </>
        )}

        {/* Help Button */}
        <div className="text-center mt-6">
          <button
            onClick={handleHelpClick}
            disabled={hintUsed}
            className={`px-8 py-4 rounded-full font-extrabold text-lg md:text-xl transition-all shadow-lg border-4 ${
              hintUsed 
                ? 'bg-gray-300 text-gray-600 cursor-not-allowed border-gray-400' 
                : 'bg-gradient-to-r from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white hover:scale-110 active:scale-95 border-white'
            }`}
            style={{ touchAction: 'manipulation' }}
            title={hintUsed ? "Hint already used" : "Get a helpful hint!"}
          >
            {hintUsed ? '💡 Hint Already Used' : '💡 I Need a Hint!'}
          </button>
          {hintUsed && (
            <p className="text-base md:text-lg text-orange-700 mt-3 font-bold bg-orange-100 rounded-lg px-4 py-2 border-2 border-orange-300 inline-block">
              💪 Still doing great! Half points earned for learning!
            </p>
          )}
        </div>

        {/* Hint Display */}
        {showHint && q && (
          <div className="bg-gradient-to-r from-blue-100 via-cyan-100 to-blue-100 border-4 border-blue-400 rounded-2xl p-5 mt-4 mx-4 shadow-xl animate-pulse">
            <div className="flex items-center justify-center gap-3">
              <span className="text-4xl md:text-5xl animate-bounce">💡</span>
              <p className="text-lg md:text-xl font-bold text-blue-900 leading-relaxed" style={{
                textShadow: '1px 1px 2px rgba(255,255,255,0.5)'
              }}>
                {generateHint(q.question, q.options, getCorrectAnswer(q), currentScreen)}
              </p>
            </div>
            <div className="text-center mt-3">
              <p className="text-sm md:text-base text-blue-700 font-semibold">
                💪 You've got this! Keep thinking!
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Animation Styles */}
      <style>{`
        .correct-bounce { animation: bounce 0.6s; }
        .incorrect-shake { animation: shake 0.5s; }
        .float { animation: float 3s ease-in-out infinite; }
        .pulse { animation: pulse 2s ease-in-out infinite; }
        .wiggle { animation: wiggle 0.5s ease-in-out; }
        .sparkle { animation: sparkle 1.5s ease-in-out infinite; }
        .rainbow { animation: rainbow 2s linear infinite; }
        
        @keyframes bounce { 
          0%,100% { transform: translateY(0); } 
          50% { transform: translateY(-20px); } 
        }
        @keyframes shake { 
          0%,100% { transform: translateX(0); } 
          25% { transform: translateX(-10px); } 
          75% { transform: translateX(10px); } 
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes wiggle {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-5deg); }
          75% { transform: rotate(5deg); }
        }
        @keyframes sparkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }
        @keyframes rainbow {
          0% { filter: hue-rotate(0deg); }
          100% { filter: hue-rotate(360deg); }
        }
        
        .particle {
          position: absolute;
          pointer-events: none;
          animation: particle-float 2s ease-out forwards;
        }
        
        @keyframes particle-float {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-100px) scale(0.5);
          }
        }
        
        @media print {
          body { margin: 0; padding: 20px; }
          .no-print { display: none !important; }
          .print-break { page-break-before: always; }
          .bg-gradient-to-br { background: white !important; }
          .shadow-xl, .shadow-lg { box-shadow: none !important; }
          .text-purple-800, .text-purple-700, .text-purple-600 { color: #000 !important; }
          .text-gray-600, .text-gray-500 { color: #333 !important; }
          .bg-white { background: white !important; border: 1px solid #ccc !important; }
          .rounded-2xl, .rounded-xl, .rounded-lg { border-radius: 4px !important; }
          .grid { display: block !important; }
          .grid > div { margin-bottom: 20px; }
        }
      `}</style>
    </div>
  );
});

GameScreen.propTypes = {
  currentScreen: PropTypes.string.isRequired,
  q: PropTypes.object,
  shuffledOptions: PropTypes.array,
  currentQuestion: PropTypes.number.isRequired,
  questionCount: PropTypes.number.isRequired,
  score: PropTypes.number.isRequired,
  hasQuestion: PropTypes.bool.isRequired,
  answerAnimation: PropTypes.string.isRequired,
  answerLocked: PropTypes.bool.isRequired,
  hintUsed: PropTypes.bool.isRequired,
  showHint: PropTypes.bool.isRequired,
  showFeedback: PropTypes.string,
  showExplanation: PropTypes.bool.isRequired,
  correctAnswer: PropTypes.string.isRequired,
  currentQuestions: PropTypes.array.isRequired,
  selectedPhonicsDifficulty: PropTypes.string.isRequired,
  setSelectedPhonicsDifficulty: PropTypes.func.isRequired,
  selectedMathDifficulty: PropTypes.string.isRequired,
  setSelectedMathDifficulty: PropTypes.func.isRequired,
  selectedReadingCategory: PropTypes.string.isRequired,
  setSelectedReadingCategory: PropTypes.func.isRequired,
  setCurrentQuestion: PropTypes.func.isRequired,
  handleAnswer: PropTypes.func.isRequired,
  handleHelpClick: PropTypes.func.isRequired,
  generateHint: PropTypes.func.isRequired,
  handleFeedbackClose: PropTypes.func.isRequired,
  navigateTo: PropTypes.func.isRequired,
  resetGameComplete: PropTypes.func.isRequired,
  playSound: PropTypes.func.isRequired,
  triggerHaptic: PropTypes.func.isRequired,
  bgColors: PropTypes.object.isRequired,
  selectedAnswers: PropTypes.array,
  handleToggleAnswer: PropTypes.func,
  handleSubmitMultipleSelect: PropTypes.func
};

GameScreen.defaultProps = {
  q: null,
  shuffledOptions: [],
  showFeedback: null,
  selectedAnswers: [],
  handleToggleAnswer: () => {},
  handleSubmitMultipleSelect: () => {}
};

GameScreen.displayName = 'GameScreen';

export default GameScreen;

